export function formatDate(date: Date) {
  const day = date.getDate()
  const month = date.getMonth() + 1
  const year = date.getFullYear()

  return `${day}/${month}/${year}`
}

/**
 * Format a date using i18n for localization
 * @param date The date to format
 * @param options Intl.DateTimeFormatOptions to customize the output
 * @returns Formatted date string based on current locale
 */
export function formatDateWithI18n(
  date: Date | string,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour12: false,
  },
) {
  if (!date)
    return ''

  // Convert string to Date if needed
  const dateObj = typeof date === 'string' ? new Date(date) : date

  // Get the current locale from i18n
  const locale = 'fr-FR'

  return new Intl.DateTimeFormat(locale, options).format(dateObj)
}

/**
 * Format a date using i18n for localization
 * @param date The date to format
 * @param options Intl.DateTimeFormatOptions to customize the output
 * @returns Formatted date string based on current locale
 */
export function formatDateWithHoursI18n(
  date: Date | string,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  },
) {
  if (!date)
    return ''

  // Convert string to Date if needed
  const dateObj = typeof date === 'string' ? new Date(date) : date

  // Get the current locale from i18n
  const locale = 'fr-FR'

  return new Intl.DateTimeFormat(locale, options).format(dateObj)
}

/**
 * Format a date using i18n for localization while preserving UTC timezone
 * @param date The date to format (UTC string or Date)
 * @param options Intl.DateTimeFormatOptions to customize the output
 * @returns Formatted date string based on current locale in UTC
 */
export function formatDateWithI18nUTC(
  date: Date | string,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour12: false,
  },
) {
  if (!date)
    return ''

  // Convert string to Date if needed
  const dateObj = typeof date === 'string' ? new Date(date) : date

  // Get the current locale from i18n
  const locale = 'fr-FR'

  // Create UTC date to avoid timezone conversion
  const utcDate = new Date(
    dateObj.getUTCFullYear(),
    dateObj.getUTCMonth(),
    dateObj.getUTCDate(),
    dateObj.getUTCHours(),
    dateObj.getUTCMinutes(),
    dateObj.getUTCSeconds(),
  )

  return new Intl.DateTimeFormat(locale, options).format(utcDate)
}

/**
 * Format a date using i18n for localization with timezone option
 * @param date The date to format
 * @param options Intl.DateTimeFormatOptions to customize the output
 * @param timeZone The timezone to use (default: 'UTC')
 * @returns Formatted date string based on current locale in specified timezone
 */
export function formatDateWithI18nInTimezone(
  date: Date | string,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour12: false,
  },
  timeZone: string = 'UTC',
) {
  if (!date)
    return ''

  // Convert string to Date if needed
  const dateObj = typeof date === 'string' ? new Date(date) : date

  // Get the current locale from i18n
  const locale = 'fr-FR'

  return new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(dateObj)
}
